import type { AudioTrackingProps } from "@/app/_providers/native-audio/NativeAudioProvider";

import { useNativeAudio } from "@/app/_providers/native-audio/NativeAudioProvider";
import { useCallback, useEffect, useRef, useState } from "react";

interface UsePreviewPlayerOptions {
  trackingProps?: AudioTrackingProps;
  onPreviewPlay?: () => void;
}

export function usePreviewPlayer(previewId: string, src: string, options?: UsePreviewPlayerOptions) {
  const { togglePlay, playingId, isPaused } = useNativeAudio();

  const isThisTrackActive = playingId === previewId;
  const isPlaying = isThisTrackActive && !isPaused;

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isStartingPlayback, setIsStartingPlayback] = useState(false);
  const progressAnimationStartedAtRef = useRef<number | null>(null);
  const wasOurTrackRef = useRef(false);
  const [progressAnimationStartedAt, setProgressAnimationStartedAt] = useState<number | null>(null);
  const isVisuallyPlaying = isPlaying || (isStartingPlayback && !isPaused);

  const onTimeUpdate = useCallback((time: number, dur: number) => {
    setCurrentTime(time);
    setDuration(dur);
  }, []);

  const onPlayingChange = useCallback((playing: boolean) => {
    if (playing) {
      setIsLoading(false);
      setIsStartingPlayback(true);
      if (progressAnimationStartedAtRef.current === null) {
        const now = performance.now();
        progressAnimationStartedAtRef.current = now;
        setProgressAnimationStartedAt(now);
      }
    } else {
      setIsStartingPlayback(false);
    }
  }, []);

  useEffect(() => {
    if (isThisTrackActive) {
      setIsStartingPlayback(false);
    }
  }, [isThisTrackActive]);

  const onEnded = useCallback(() => {
    setIsStartingPlayback(false);
    progressAnimationStartedAtRef.current = null;
    setProgressAnimationStartedAt(null);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const onError = useCallback(() => {
    setIsLoading(false);
    setIsStartingPlayback(false);
    progressAnimationStartedAtRef.current = null;
    setProgressAnimationStartedAt(null);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  useEffect(() => {
    if (isThisTrackActive) {
      wasOurTrackRef.current = true;
    }
  }, [isThisTrackActive]);

  useEffect(() => {
    if (wasOurTrackRef.current && playingId !== null && playingId !== previewId) {
      wasOurTrackRef.current = false;
      progressAnimationStartedAtRef.current = null;
      setProgressAnimationStartedAt(null);
      setCurrentTime(0);
      setDuration(0);
      setIsLoading(false);
      setIsStartingPlayback(false);
    }
  }, [playingId, previewId]);

  const handleTogglePlay = useCallback(() => {
    if (!isThisTrackActive && !wasOurTrackRef.current) {
      options?.onPreviewPlay?.();
      setIsLoading(true);
    }

    togglePlay({
      id: previewId,
      url: src,
      trackingProps: options?.trackingProps,
      onTimeUpdate,
      onPlayingChange,
      onEnded,
      onError,
    });
  }, [isThisTrackActive, options, togglePlay, previewId, src, onTimeUpdate, onPlayingChange, onEnded, onError]);

  return {
    isPlaying: isVisuallyPlaying,
    isLoading,
    handleTogglePlay,
    progressRingProps: {
      currentTime,
      duration,
      isActive: isVisuallyPlaying,
      progressAnimationStartedAt,
    },
  };
}
